import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * A thin, reusable file-upload control for the DVM layout builder + published
 * dashboard. It renders a hidden `<input type="file">` + a button (+ optional
 * title/status) and simply EMITS the picked file — the parent owns the POST to
 * the compute API and how the response drives its targets (that logic differs
 * between the builder preview and the published view, so it stays in the
 * parent). All labels/accept/state are `@Input`-driven so nothing is hardcoded.
 */
@Component({
  selector: 'app-dvm-file-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dvm-file-upload.component.html',
  styleUrl: './dvm-file-upload.component.scss',
})
export class DvmFileUploadComponent {
  /** Button text (e.g. "Upload file"). */
  @Input() buttonLabel = 'Upload file';
  /** Accepted file types for the picker (e.g. ".xlsx,.xls,.csv"). */
  @Input() accept = '.xlsx,.xls,.csv';
  @Input() buttonStyle: Record<string, string> = {};
  /** Optional heading shown above the button (the tile title). */
  @Input() title = '';
  /** True while the parent's upload/compute call is in flight. */
  @Input() uploading = false;
  /** Status message shown under the button (parent-controlled). */
  @Input() message = '';
  /** How many targets this uploader drives — shown as a hint when > 0. */
  @Input() targetCount = 0;
  /** Compact mode (toolbar button only — no title/hint) for in-table use. */
  @Input() compact = false;

  /** Emitted with the picked File when the user selects one. The parent POSTs
   *  it and updates `uploading`/`message`. */
  @Output() filePicked = new EventEmitter<File>();

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    // Reset so the SAME file can be re-picked (change won't fire otherwise).
    if (input) input.value = '';
    if (file) this.filePicked.emit(file);
  }
}
